


























import { Component, Prop, Vue } from "vue-property-decorator";
import { Department } from "@/typings";

interface Book {
  key: string;
  code: string;
  title: string;
  tip: string;
  to: string;
  light: boolean;
  style: { [property: string]: string };
}

// Book cloth, by department: [cover, whether the cover is light enough to need dark lettering]
const CLOTHS: [string, boolean][] = [
  ["#7a2c25", false], // oxblood
  ["#24395c", false], // navy
  ["#2e5134", false], // forest
  ["#6b4a1f", false], // tobacco
  ["#1f5a63", false], // teal
  ["#57385d", false], // plum
  ["#8a3b20", false], // rust
  ["#3f4a57", false], // slate
  ["#c49a3a", true], // mustard
  ["#d8ccae", true], // natural linen
];

function hash(text: string): number {
  let h = 0;
  for (let i = 0; i < text.length; i++) {
    h = (h * 31 + text.charCodeAt(i)) >>> 0;
  }
  return h;
}

// iBooks' wooden shelf, above the footer, holding the courses in the current course set as
// cloth-bound books. Each book opens its course; with no courses, the shelf holds the QuACS handbook.
@Component
export default class SkeuomorphismDecor extends Vue {
  @Prop({ required: true }) readonly place!: string;

  get selectedCrns(): Set<number> {
    const schedule = this.$store.state.schedule;
    const set = schedule.courseSets[schedule.currentTerm]?.[
      schedule.currentCourseSet
    ] as { [crn: string]: boolean } | undefined;
    return new Set(
      set
        ? Object.keys(set)
            .filter((crn) => set[crn])
            .map(Number)
        : []
    );
  }

  get label(): string {
    return this.$store.state.schedule.currentCourseSet;
  }

  get books(): Book[] {
    const selected = this.selectedCrns;
    const books: Book[] = [];
    if (selected.size > 0) {
      for (const dept of this.$store.state.departments as Department[]) {
        for (const course of dept.courses) {
          if (course.sections.some((section) => selected.has(section.crn))) {
            books.push(
              this.book(
                `${course.subj}-${course.crse}`,
                course.title,
                course.subj
              )
            );
          }
        }
      }
    }
    if (books.length === 0) {
      // An empty shelf holds the handbook, bound in QuACS gold
      const handbook = this.book("QUACS-101", "The QuACS Handbook", "QUACS");
      return [
        {
          ...handbook,
          to: "/",
          tip: "Add courses to your schedule and they'll stand on this shelf",
          light: true,
          style: { ...handbook.style, "--sk-cover": "#f0ad1c" },
        },
      ];
    }
    return books.sort((a, b) => a.code.localeCompare(b.code));
  }

  book(code: string, title: string, subj: string): Book {
    const [cover, light] = CLOTHS[hash(subj) % CLOTHS.length];
    // Books on a real shelf are never quite the same size
    const size = hash(code);
    return {
      key: `${code} ${title}`,
      code: code.replace("-", " "),
      title,
      tip: `${code.replace("-", " ")}: ${title}`,
      to: `/search?${encodeURIComponent(code)}`,
      light,
      style: {
        "--sk-cover": cover,
        "--sk-w": `${80 + (size % 3) * 6}px`,
        "--sk-h": `${108 + ((size >> 3) % 4) * 5}px`,
      },
    };
  }
}
